import React, { useCallback, useEffect, useMemo } from 'react';
import type { CardHeaderProps } from '@mui/material';
import { Box, CardActions, CardContent, Step, StepLabel, Stepper, Typography } from '@mui/material';
import type { AnyObjectSchema } from 'yup';
import type { TranslationPath } from 'c-translation';
import { Translate, useCommonTranslation } from 'c-translation';
import { Button, TransparentCard } from 'c-components';
import { useFormContext } from 'react-hook-form';
import { useEntityData } from 'c-data';
import type { Buyer } from 'c-sdk';
import { CampaignFormNames } from 'c-main/Types';
import { DetailsStep, ChooseDisplaysStep, CostingStep, SummaryStep, StepContent } from './Steps';
import {
    NewCampaignStep as Steps,
    newCampaignStepCount as totalSteps,
    stepBlockers,
} from './newCampaignSteps';

type Props = {
    step: Steps;
    onStepChange: (step: Steps) => void;
    onBuyerChange: (buyer: Buyer) => void;
    /** What the current step must pass; anything it refuses is listed under the button. */
    validationSchema: AnyObjectSchema;

    campaignCreate: React.ReactNode;
};

const blockersId = 'new-campaign-step-blockers';

const stepTitles: Record<Steps, TranslationPath> = {
    [Steps.Details]: 'Modules.Main.Campaigns.Create.stepDetailsTitle',
    [Steps.ChooseDisplays]: 'Modules.Main.Campaigns.Create.stepLineItemsTitle',
    [Steps.Schedule]: 'Modules.Main.Campaigns.Create.stepScheduleTitle',
    [Steps.Summary]: 'Modules.Main.Campaigns.Create.stepSummaryTitle',
};

const stepMaxWidths = {
    [Steps.Details]: 1100,
    [Steps.ChooseDisplays]: 1200,
    [Steps.Schedule]: 1200,
    [Steps.Summary]: 600,
};

const cardTitleTypographyProps: CardHeaderProps['titleTypographyProps'] = { variant: 'h3' };

const NewCampaignStepper: React.FC<Props> = ({
    step: currentStep,
    onStepChange,
    onBuyerChange,
    validationSchema,
    campaignCreate,
}) => {
    const { watch } = useFormContext();
    const t = useCommonTranslation();

    const buyerId = watch(CampaignFormNames.buyer);
    const { getById: GetBuyer } = useEntityData<Buyer>('Buyer');
    const buyer = GetBuyer({ id: buyerId });

    useEffect(() => {
        onBuyerChange(buyer);
    }, [buyer, onBuyerChange]);

    const showLast = currentStep === totalSteps - 1;
    const hasNext = currentStep < totalSteps - 1;
    const hasPrev = currentStep > 0;

    // checked against every value, touched or not, so the reasons are there before any typing
    const blockers = stepBlockers(validationSchema, watch());
    const blockerTexts = blockers.map(blocker => t(blocker.path, blocker.params));

    const goNext = useCallback(() => {
        if (hasNext) {
            onStepChange(currentStep + 1);
        }
    }, [currentStep, hasNext, onStepChange]);

    const goPrev = useCallback(() => {
        if (hasPrev) {
            onStepChange(currentStep - 1);
        }
    }, [currentStep, hasPrev, onStepChange]);

    const visibleStep = currentStep;

    const stepTitle = useCommonTranslation(stepTitles[visibleStep]);

    const stepLabel = useCommonTranslation('Modules.Main.Campaigns.Create.currentStepLabel', {
        current: currentStep + 1,
        total: totalSteps,
    });
    const stepCountTitle = useMemo(
        () => [
            <Typography key="step-count" variant="h4">
                {stepLabel}
            </Typography>,
        ],
        [stepLabel],
    );

    const memoStepper = useMemo(
        () => (
            <Stepper alternativeLabel activeStep={currentStep} sx={{ py: 2 }}>
                <Step>
                    <StepLabel>
                        <Translate path="Modules.Main.Campaigns.Create.detailsStepLabel" />
                    </StepLabel>
                </Step>
                <Step>
                    <StepLabel>
                        <Translate path="Modules.Main.Campaigns.Create.chooseDisplaysStepLabel" />
                    </StepLabel>
                </Step>
                <Step>
                    <StepLabel>
                        <Translate path="Modules.Main.Campaigns.Create.scheduleStepLabel" />
                    </StepLabel>
                </Step>
                <Step>
                    <StepLabel>
                        <Translate path="Modules.Main.Campaigns.Create.summaryStepLabel" />
                    </StepLabel>
                </Step>
            </Stepper>
        ),
        [currentStep],
    );

    const memoSteps = useMemo(
        () => (
            <>
                {visibleStep === Steps.Details && (
                    <StepContent visible>
                        <DetailsStep />
                    </StepContent>
                )}
                {visibleStep === Steps.ChooseDisplays && (
                    <StepContent visible>
                        <ChooseDisplaysStep />
                    </StepContent>
                )}
                {visibleStep === Steps.Schedule && (
                    <StepContent visible>
                        <CostingStep />
                    </StepContent>
                )}
                {visibleStep === Steps.Summary && (
                    <StepContent visible>
                        <SummaryStep />
                        {campaignCreate}
                    </StepContent>
                )}
            </>
        ),
        [campaignCreate, visibleStep],
    );

    const isBlocked = blockerTexts.length > 0;
    // not memoised: it changes with every value the blockers read
    const actions = (
        <CardActions sx={{ pt: 2, px: 0, flexDirection: 'column', alignItems: 'stretch' }}>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Button variant="outlined" onClick={goPrev} disabled={!hasPrev}>
                    <Translate path="Modules.Main.Campaigns.Create.prevStepLabel" />
                </Button>
                {!showLast && (
                    <Button
                        onClick={goNext}
                        disabled={!hasNext || isBlocked}
                        aria-describedby={isBlocked ? blockersId : undefined}
                    >
                        <Translate path="Modules.Main.Campaigns.Create.nextStepLabel" />
                    </Button>
                )}
                {showLast && (
                    <Button
                        type="submit"
                        disabled={isBlocked}
                        aria-describedby={isBlocked ? blockersId : undefined}
                    >
                        <Translate path="Modules.Main.Campaigns.Create.createCampaignLabel" />
                    </Button>
                )}
            </Box>
            {isBlocked && (
                <Box id={blockersId} sx={{ alignSelf: 'flex-end', mt: 1, ml: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                        <Translate
                            path={
                                showLast
                                    ? 'Modules.Main.Campaigns.Create.Blockers.createIntro'
                                    : 'Modules.Main.Campaigns.Create.Blockers.nextIntro'
                            }
                        />
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {blockerTexts.map(text => (
                            <Typography
                                key={text}
                                component="li"
                                variant="body2"
                                color="text.secondary"
                            >
                                {text}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}
        </CardActions>
    );

    return (
        <>
            <Box
                minHeight={200}
                maxWidth={stepMaxWidths[visibleStep]}
                width="100%"
                mx="auto"
                position="relative"
                mt={2}
            >
                <TransparentCard
                    title={stepTitle}
                    titleTypographyProps={cardTitleTypographyProps}
                    controls={stepCountTitle}
                    controlsUnderTitle
                >
                    <CardContent>
                        <Box width="100%" display="flex">
                            <Box width="100%">
                                {memoStepper}
                                {memoSteps}
                                {actions}
                            </Box>
                        </Box>
                    </CardContent>
                </TransparentCard>
            </Box>
        </>
    );
};

export default NewCampaignStepper;
